import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { PRODUCTS } from "@/lib/products";
import SanaaMark from "@/components/SanaaMark";
import LangSwitcher from "@/components/LangSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import ProductMark from "@/components/ProductMark";
import UsageMeter from "@/components/UsageMeter";
import { ChevronDown, LogOut, LayoutDashboard, CreditCard } from "lucide-react";

// Shared shell used by every product workspace.
export default function WorkspaceShell({ product, children, usageRefresh = 0 }) {
  const { user, logout } = useAuth();
  const { lang } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      style={{ "--product": product.accent }}
    >
      <header
        className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur-xl"
      >
        <div className="mx-auto max-w-[1600px] px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/"
              data-testid="ws-home"
              className="flex items-center gap-2 shrink-0"
            >
              <SanaaMark size={22} />
              <span className="hidden sm:inline font-display text-sm tracking-tight">Sanaa AI</span>
            </Link>
            <div className="text-muted-foreground select-none">/</div>
            <div className="relative">
              <button
                onClick={() => setOpen((o) => !o)}
                data-testid="ws-product-switcher"
                className="inline-flex items-center gap-2 rounded-sm border border-border/60 bg-card/40 hover:bg-card px-2.5 py-1.5"
                style={{ borderColor: `${product.accent}40` }}
              >
                <ProductMark product={product} size={24} />
                <span className="font-display text-sm font-medium">{product.name}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              {open && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                  <div className="absolute start-0 mt-2 w-72 rounded-sm border border-border bg-popover/95 backdrop-blur-xl p-1.5 shadow-2xl z-50">
                    {PRODUCTS.map((p) => (
                      <Link
                        key={p.key}
                        to={p.route}
                        onClick={() => setOpen(false)}
                        data-testid={`ws-switch-to-${p.key}`}
                        className={`flex items-center gap-3 rounded-sm px-2.5 py-2 hover:bg-accent ${p.key === product.key ? "bg-accent" : ""}`}
                      >
                        <ProductMark product={p} size={28} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{p.name}</div>
                          <div className="text-xs text-muted-foreground truncate">vs {p.disrupt}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <UsageMeter product={product.key} refreshKey={usageRefresh} className="hidden md:block min-w-[180px]" />
            <button
              data-testid="ws-back-to-dashboard"
              onClick={() => nav("/app")}
              className="inline-flex h-9 items-center gap-2 rounded-sm border border-border/60 bg-card/40 hover:bg-card px-3 text-sm"
            >
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden sm:inline">Suite</span>
            </button>
            <button
              data-testid="ws-billing"
              onClick={() => nav("/app/billing")}
              className="hidden sm:inline-flex h-9 items-center gap-2 rounded-sm border border-border/60 bg-card/40 hover:bg-card px-3 text-sm"
            >
              <CreditCard className="h-4 w-4" /> Billing
            </button>
            <LangSwitcher />
            <ThemeToggle />
            <button
              data-testid="ws-logout"
              onClick={() => {
                logout();
                nav("/");
              }}
              aria-label="Logout"
              className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-border/60 bg-card/40 hover:bg-card"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1600px] px-5 py-8">{children}</main>
    </div>
  );
}
